import { Alert, Box, Button, Card, CardActions, CardContent, Grid, Typography } from '@mui/material'
import React, { useState } from 'react'
import type { CurrencyExchangeInstructionDetails } from '../../../../api/schema-dtos/CurrencyExchangeInstructionDomainDtos'
import type { AssetHolderIdProps } from '../../pages/DashboardHome'
import CancelExchangeInstructionComponent from './CancelExchangeInstructionComponent'

interface ExchangeInstructionComponentProps extends AssetHolderIdProps {
  instructions: CurrencyExchangeInstructionDetails[]
  maxCardWidth: number
}

const ExchangeInstructionsComponent: React.FC<ExchangeInstructionComponentProps> = ({
  assetHolderId,
  instructions,
  maxCardWidth
}: ExchangeInstructionComponentProps) => {
  const [instructionToCancel, setInstructionToCancel] = useState<number | null>(null)
  return (
    <>
      <Typography variant={'body1'} sx={{ mb: 1, maxWidth: maxCardWidth }}>
        You can convert your ZAR balance to BTC. Please follow the process below::
      </Typography>
      <Typography component={'ul'} variant={'body1'} sx={{ mb: 2, maxWidth: maxCardWidth }} >
        <li>Your KYC status must be up to date prior to scheduling a BTC payout (see &apos;My Profile&apos; tab).</li>
        <li>Request to schedule a BTC payout by clicking the button below.</li>
        <li>Your ZAR balance will be converted and transferred to your BTC wallet the following Monday.</li>
        <li>The exchange rate is determined at the spot rate on the Monday when the conversion is processed.</li>
        <li>Once the transfer is complete, you can schedule a standard BTC cash out.</li>
        <li><b>Please note</b>: Each request scheduled will result in the entire ZAR wallet balance being paid out in BTC. BTC balances cannot be converted to ZAR.</li>
      </Typography>

      { instructions.length === 0 &&
        <Box sx={{ maxWidth: maxCardWidth }}>
          <Alert severity="info" >There are currently no pending currency conversion requests.</Alert>
        </Box>
      }

      { instructions.map((instruction: CurrencyExchangeInstructionDetails, index: number) => (

        <Card sx={{ maxWidth: maxCardWidth, mb: 2 }} variant={'outlined'} key={instruction.currencyExchangeInstructionId}>
          <CardContent>
            <Grid container columnSpacing={2}>

              <Grid item md={12} xs={12}>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography variant={'body1'}>Status</Typography>
                  <Typography variant={'body1'}>{instruction.state}</Typography>
                </Box>
              </Grid>
              <Grid item md={12} xs={12}>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography variant={'body1'}>From {instruction.fromCurrencyDisplayCode}</Typography>
                  <Typography variant={'body2'}>{instruction.fromCurrencyAmountFormatted}</Typography>
                </Box>
              </Grid>
              <Grid item md={12} xs={12}>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography variant={'body1'}>To {instruction.toCurrencyDisplayCode}</Typography>
                  { instruction.toCurrencyAmountFormatted === ''
                    ? (
                      <Typography variant={'body1'} fontStyle={'italic'}>awaiting exchange rate</Typography>
                      )
                    : (
                      <Typography variant={'body2'}>{instruction.toCurrencyAmountFormatted}</Typography>
                      )
                  }
                </Box>
              </Grid>
              <Grid item md={12} xs={12}>
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography variant={'body1'}>Batch date</Typography>
                  <Typography variant={'body2'}>{instruction.nextMondayProcessingDate}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>

          <CardActions>
            { instruction.isCancellable && (
              <Box sx={{ display: 'flex', width: '100%' }} flexDirection={'row'} justifyContent={'flex-end'}>
                <Button variant={'outlined'} onClick={() => { setInstructionToCancel(instruction.currencyExchangeInstructionId) }}>
                  Cancel ZAR Payout In BTC
                </Button>
              </Box>
            )}
          </CardActions>
        </Card>

      ))}

      {instructionToCancel !== null &&
        <CancelExchangeInstructionComponent
          assetHolderId={assetHolderId}
          instructionId={instructionToCancel}
          onCloseDialog={() => { setInstructionToCancel(null) }}
        />
      }
    </>
  )
}

export default ExchangeInstructionsComponent
